import { Map, Popup, Sidebar, Source } from '@/components/controls';
import { useFilteredSnapshot, useInterval } from '@/hooks';
import { sortOptions, types } from '@/utils/constants';
import { Box } from '@mui/material';
import { addSeconds, startOfSecond } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ReplayButton } from './ReplayButton';
import { parseKeyValues } from '@/utils';

const speedOptions = [
  { step: 5, label: '5s' },
  { step: 60, label: '1m' },
  { step: 600, label: '10m' },
  { step: 3600, label: '1h' },
];

export function Replay() {
  const [searchParams, setSearchParams] = useSearchParams();
  const time = useMemo(
    () => new Date(searchParams.get('time') ?? startOfSecond(new Date())),
    [searchParams],
  );
  const [search, setSearch] = useState(
    Object.fromEntries(Array.from(Object.keys(types), (key) => [key, ''])),
  );
  const [sort, setSort] = useState(
    Object.fromEntries(
      Array.from(Object.keys(types), (key) => [
        key,
        { sortBy: sortOptions[key][0].value, sortDesc: false },
      ]),
    ),
  );
  const [filter, setFilter] = useState(
    Object.fromEntries(Array.from(Object.keys(types), (key) => [key, {}])),
  );
  const { snapshot, filterValues, isFetching } = useFilteredSnapshot(
    time,
    search,
    sort,
    filter,
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [mapHoverInfo, setMapHoverInfo] = useState(null);
  const [listHoverInfo, setListHoverInfo] = useState(null);
  const [speed, setSpeed] = useState(0);
  const [hiddenSources, setHiddenSources] = useState([]);
  const [last, setLast] = useState(startOfSecond(new Date()));

  useInterval(
    () => {
      const now = startOfSecond(new Date());
      setLast(now);
      if (time >= now) {
        setIsPlaying(false);
        setSearchParams({ time: now.toISOString() }, { replace: true });
      } else {
        setSearchParams(
          {
            time: addSeconds(time, speedOptions[speed].step).toISOString(),
          },
          { replace: true },
        );
      }
    },
    isPlaying && !isFetching
      ? Math.max(0, 5000 - (startOfSecond(new Date()) - last))
      : null,
  );

  const handleMouseMove = useCallback((event) => {
    const hoveredFeature =
      event.features && event.features[0]
        ? {
            ...event.features[0],
            properties: parseKeyValues(event.features[0].properties),
          }
        : null;

    setMapHoverInfo({
      feature: hoveredFeature,
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
  }, []);

  function handlePlayPauseClick() {
    setIsPlaying((isPlaying) => !isPlaying);
  }

  function handleSpeedClick() {
    setSpeed((speed) => (speed === speedOptions.length - 1 ? 0 : speed + 1));
  }

  function handleTimeChange(value) {
    setSearchParams({ time: value.toISOString() }, { replace: true });
  }

  function handleTabClick(event, value) {
    setHiddenSources((hiddenSources) =>
      hiddenSources.includes(value)
        ? hiddenSources.filter((source) => source !== value)
        : [...hiddenSources, value],
    );
  }

  function handleListItemHover(hoverInfo) {
    setListHoverInfo(hoverInfo);
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <title>IR3 | Replay</title>
      <Box
        sx={{
          width: 320,
          height: 'calc(100vh - 48px)',
          borderRight: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Sidebar
          featureCollections={snapshot}
          hoveredFeature={mapHoverInfo?.feature}
          hiddenTabs={hiddenSources}
          onTabClick={handleTabClick}
          onHover={handleListItemHover}
          search={search}
          onSearchChange={setSearch}
          sort={sort}
          onSortChange={setSort}
          filter={filter}
          onFilterChange={setFilter}
          filterValues={filterValues}
        />
      </Box>
      <Map
        sx={{ flexGrow: 1, height: 'calc(100vh - 48px)' }}
        interactiveLayerIds={Object.keys(snapshot).map(
          (key) => `${key}-symbol-layer`,
        )}
        onMouseMove={handleMouseMove}
        searchable
      >
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <ReplayButton
            time={time}
            onTimeChange={handleTimeChange}
            isPlaying={isPlaying}
            togglePlaying={handlePlayPauseClick}
            speed={speedOptions[speed].label}
            onSpeedClick={handleSpeedClick}
          />
        </Box>
        {Object.entries(snapshot)
          .sort(([id1], [id2]) => types[id1].zIndex - types[id2].zIndex)
          .map(([id, data]) => (
            <Source
              key={id}
              id={id}
              data={data}
              visibility={hiddenSources.includes(id) ? 'none' : 'visible'}
            />
          ))}
        {mapHoverInfo?.feature && <Popup hoverInfo={mapHoverInfo} />}
        {listHoverInfo && <Popup hoverInfo={listHoverInfo} />}
      </Map>
    </Box>
  );
}
